import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { IS_NATIVE_APP } from "../config/appTarget.js";
import { getJson, setJson } from "../lib/appStorage.js";

const AUTH_RETURN_TO_KEY = "auth_return_to";

/**
 * Listens for `appUrlOpen` events from `@capacitor/app` (custom-scheme AND
 * Universal Links / App Links both funnel through this same event).
 *
 * Two distinct cases:
 *  - Auth0 redirect (custom scheme `com.pawseum.app://` OR the Auth0 domain
 *    itself) -> `handleRedirectCallback` + restore the pre-login route.
 *  - Any other `https://pawseum.com/...` tap (email CTA, notification, shared
 *    link) -> a real Universal Link -> just navigate to that path/query.
 *
 * Must be rendered inside both <BrowserRouter> and <Auth0Provider>.
 */
export function useAuth0DeepLink(): void {
  const { handleRedirectCallback } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (!IS_NATIVE_APP) return;

    let cleanup: (() => void) | undefined;

    import("@capacitor/app").then(({ App: CapApp }) => {
      const listenerPromise = CapApp.addListener(
        "appUrlOpen",
        async ({ url }: { url: string }) => {
          const isAuthCallback = url.startsWith("com.pawseum.app://");
          const isPawseumUniversalLink = !isAuthCallback && url.includes("pawseum.com");

          if (!isAuthCallback && !isPawseumUniversalLink) return;

          if (isAuthCallback) {
            // Close the SFSafariViewController sheet before navigating
            try {
              const { Browser } = await import("@capacitor/browser");
              await Browser.close();
            } catch {
              // Browser may already be closed or not in use — safe to ignore
            }

            try {
              await handleRedirectCallback(url);
            } catch (err) {
              console.error("[Auth0DeepLink] handleRedirectCallback failed", err);
            }

            const returnTo = (await getJson<string>(AUTH_RETURN_TO_KEY)) ?? "/";
            await setJson(AUTH_RETURN_TO_KEY, null);
            navigate(returnTo, { replace: true });
            return;
          }

          // Universal Link to an ordinary in-app page (e.g. tapped from an
          // email CTA) — navigate straight to it, no Auth0 involvement.
          try {
            const { pathname, search, hash } = new URL(url);
            navigate(`${pathname}${search}${hash}`);
          } catch (err) {
            console.error("[Auth0DeepLink] failed to parse universal link", url, err);
          }
        },
      );

      listenerPromise.then((handle) => {
        cleanup = () => handle.remove();
      });
    });

    return () => {
      cleanup?.();
    };
  }, [handleRedirectCallback, navigate]);
}

/** Store the current path so we can restore it after the Auth0 redirect. */
export async function saveAuthReturnTo(path: string): Promise<void> {
  await setJson(AUTH_RETURN_TO_KEY, path);
}
